import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { supabase } from '../../../lib/supabaseClient';
import { uploadSDEFile } from '../../Components/SDE24/sdeSupabaseService';
import Spinner from '../../Atoms/Spinner';

export const SDO_ACCORD_PDF_URL = '/documents/Accord_confidentialite_SDO2026_designers.pdf';

const PageContainer = styled.div`
    max-width: 720px; margin: 0 auto; padding: 40px 20px 80px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;
const LogoImg = styled.img` display: block; margin: 0 auto 20px; max-width: 180px; `;
const PageTitle = styled.h1` text-align: center; font-size: 1.6rem; color: #1a1a2e; margin-bottom: 8px; font-weight: 700; `;
const PageSubtitle = styled.p` text-align: center; font-size: 0.95rem; color: #666; margin-bottom: 32px; `;
const DesignerBadge = styled.div`
    text-align: center; font-size: 1.1rem; font-weight: 600; color: rgb(24, 51, 189);
    background: #f0f4ff; border: 1px solid #d0d8ff; border-radius: 8px; padding: 12px 20px; margin-bottom: 28px;
`;
const SectionCard = styled.div` background: white; border: 1px solid #e0e0e0; border-radius: 10px; padding: 24px; margin-bottom: 20px; `;
const SectionTitle = styled.h2`
    font-size: 1.1rem; color: rgb(24, 51, 189); margin-bottom: 20px; font-weight: 700;
    border-bottom: 2px solid rgb(24, 51, 189); padding-bottom: 8px;
`;
const DownloadLink = styled.a`
    display: inline-flex; align-items: center; gap: 8px; padding: 12px 24px;
    background: linear-gradient(135deg, rgb(24, 51, 189), rgb(18, 40, 155)); color: white; border-radius: 8px;
    text-decoration: none; font-weight: 600; font-size: 14px; transition: all 0.2s;
    &:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(24,51,189,0.3); color: white; }
`;
const DropZone = styled.div`
    border: 2px dashed ${p => p.hasFile ? '#27ae60' : '#d0d0d0'}; background: ${p => p.hasFile ? '#f0fff4' : '#fafafa'};
    border-radius: 10px; padding: 32px 20px; text-align: center; cursor: pointer; transition: all 0.2s; margin-bottom: 16px;
    &:hover { border-color: ${p => p.hasFile ? '#27ae60' : 'rgb(24, 51, 189)'}; background: ${p => p.hasFile ? '#e8f5e9' : '#f0f4ff'}; }
`;
const DropZoneLabel = styled.div` font-size: 14px; color: ${p => p.hasFile ? '#27ae60' : '#888'}; font-weight: ${p => p.hasFile ? '600' : '400'}; `;
const SubmitBtn = styled.button`
    display: block; width: 100%; padding: 14px; background: linear-gradient(135deg, #27ae60, #229954); color: white;
    border: none; border-radius: 8px; font-size: 16px; font-weight: 600; cursor: pointer; transition: all 0.2s;
    &:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(39,174,96,0.3); }
    &:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
`;
const Alert = styled.div`
    padding: 14px 20px; border-radius: 8px; font-size: 14px; margin-bottom: 20px;
    background: ${p => p.type === 'success' ? '#d4edda' : p.type === 'error' ? '#f8d7da' : '#fff3cd'};
    color: ${p => p.type === 'success' ? '#155724' : p.type === 'error' ? '#721c24' : '#856404'};
    border: 1px solid ${p => p.type === 'success' ? '#c3e6cb' : p.type === 'error' ? '#f5c6cb' : '#ffeeba'};
`;
const NotFound = styled.div` text-align: center; padding: 60px 20px; h2 { font-size: 1.4rem; color: #333; margin-bottom: 12px; } p { color: #666; } `;
const AlreadyUploaded = styled.div`
    padding: 16px; background: #d4edda; border: 1px solid #c3e6cb; border-radius: 8px; margin-bottom: 16px;
    display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 12px;
`;

const SDOAccordConfidentialite = () => {
    const [searchParams] = useSearchParams();
    const rid = searchParams.get('rid');
    const [reg, setReg] = useState(null);
    const [loading, setLoading] = useState(true);
    const [file, setFile] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!rid) { setLoading(false); return; }
        supabase.from('sde_registrations').select('id,company_name,representative_1,metadata,population_type')
            .eq('id', rid).eq('population_type', 'pop_b').maybeSingle()
            .then(({ data }) => { setReg(data || null); setLoading(false); });
    }, [rid]);

    const handleFileSelect = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.type !== 'application/pdf') { setError('Seuls les fichiers PDF sont acceptés.'); return; }
        if (f.size > 10 * 1024 * 1024) { setError('Le fichier ne doit pas dépasser 10 Mo.'); return; }
        setFile(f); setError(null);
    };

    const handleUpload = async () => {
        if (!file || !reg) return;
        setSubmitting(true); setError(null);
        try {
            const { data, error: upErr } = await uploadSDEFile(file, `accords-signes/${reg.id}_${Date.now()}.pdf`);
            if (upErr) { setError("Erreur lors de l'upload : " + upErr.message); return; }
            const signedAt = new Date().toISOString();
            const { data: fresh } = await supabase.from('sde_registrations').select('metadata').eq('id', reg.id).single();
            const metadata = { ...(fresh?.metadata || reg.metadata || {}), accord_signe_url: data.publicUrl, accord_signe_at: signedAt };
            const { error: updErr } = await supabase.from('sde_registrations').update({ metadata }).eq('id', reg.id);
            if (updErr) { setError('Erreur lors de la sauvegarde.'); return; }
            setReg(prev => ({ ...prev, metadata }));
            setSubmitted(true); setFile(null);
        } catch (err) {
            setError('Erreur inattendue : ' + err.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <PageContainer><div style={{ textAlign: 'center', padding: '60px 0' }}><Spinner /></div></PageContainer>;

    if (!reg) {
        return (
            <PageContainer>
                <LogoImg src="/img/logo.png" alt="Le FRENCH DESIGN" />
                <NotFound><h2>Page introuvable</h2><p>Le lien utilisé est invalide ou a expiré.</p></NotFound>
            </PageContainer>
        );
    }

    const rep = reg.representative_1 || {};
    const signedUrl = reg.metadata?.accord_signe_url;
    const signedAt = reg.metadata?.accord_signe_at;

    return (
        <PageContainer>
            <LogoImg src="/img/logo.png" alt="Le FRENCH DESIGN" />
            <PageTitle>Accord de confidentialité</PageTitle>
            <PageSubtitle>Speed Dating Design Objet — mardi 29 septembre 2026</PageSubtitle>

            <DesignerBadge>
                {reg.company_name}
                {(rep.firstname || rep.name) && ` — ${[rep.firstname, rep.name].filter(Boolean).join(' ')}`}
            </DesignerBadge>

            {error && <Alert type="error">{error}</Alert>}
            {submitted && <Alert type="success">Accord signé envoyé avec succès. Merci !</Alert>}

            <SectionCard>
                <SectionTitle>1. Télécharger l'accord</SectionTitle>
                <p style={{ fontSize: 14, color: '#555', marginBottom: 16, lineHeight: 1.6 }}>
                    Téléchargez l'accord de confidentialité, complétez la première page (raison sociale, siège social, SIRET, représentant), datez et signez la page 3, puis déposez la version signée ci-dessous.
                </p>
                <DownloadLink href={SDO_ACCORD_PDF_URL} target="_blank" rel="noreferrer">Télécharger l'accord (PDF)</DownloadLink>
            </SectionCard>

            <SectionCard>
                <SectionTitle>2. Déposer l'accord signé</SectionTitle>
                {signedUrl && !submitted && (
                    <AlreadyUploaded>
                        <div>
                            <strong style={{ color: '#155724' }}>Accord signé déjà envoyé</strong>
                            <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
                                {signedAt && `Envoyé le ${new Date(signedAt).toLocaleDateString('fr-FR')}`}
                            </div>
                        </div>
                        <a href={signedUrl} target="_blank" rel="noreferrer" style={{ color: '#155724', fontWeight: 600, fontSize: 13 }}>Voir le PDF</a>
                    </AlreadyUploaded>
                )}
                <p style={{ fontSize: 14, color: '#555', marginBottom: 16, lineHeight: 1.6 }}>
                    {signedUrl ? "Vous pouvez remplacer l'accord signé en déposant une nouvelle version." : "Déposez l'accord signé au format PDF (10 Mo maximum)."}
                </p>
                <DropZone hasFile={!!file} onClick={() => document.getElementById('accord-file-input').click()}>
                    <input id="accord-file-input" type="file" accept="application/pdf" style={{ display: 'none' }} onChange={handleFileSelect} />
                    <DropZoneLabel hasFile={!!file}>{file ? file.name : 'Cliquez pour sélectionner le PDF signé'}</DropZoneLabel>
                    {!file && <div style={{ fontSize: 12, color: '#aaa', marginTop: 8 }}>PDF — 10 Mo max</div>}
                </DropZone>
                <SubmitBtn onClick={handleUpload} disabled={!file || submitting}>
                    {submitting ? 'Envoi en cours...' : (signedUrl ? "Remplacer l'accord signé" : "Envoyer l'accord signé")}
                </SubmitBtn>
            </SectionCard>

            <p style={{ textAlign: 'center', fontSize: 13, color: '#888' }}>
                Une question ? <a href="mailto:design@via.fr" style={{ color: 'rgb(24, 51, 189)' }}>design@via.fr</a>
            </p>
        </PageContainer>
    );
};

export default SDOAccordConfidentialite;
